import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { create } from "zustand";
import { cn } from "@/lib/utils";

type ToastKind = "default" | "success" | "error" | "warning";
interface ToastItem {
  id: string;
  kind: ToastKind;
  title?: string;
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastStore {
  items: ToastItem[];
  push: (t: Omit<ToastItem, "id">) => string;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: (t) => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    set((s) => ({ items: [...s.items, { id, duration: 3500, ...t }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: "success", title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: "error", title, description, duration: 6000 }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: "warning", title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ kind: "default", title, description }),
  custom: (t: Omit<ToastItem, "id">) => useToastStore.getState().push(t),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {items.map((t) => (
        <ToastPrimitive.Root
          key={t.id}
          duration={t.duration}
          onOpenChange={(open) => !open && dismiss(t.id)}
          className={cn(
            "pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-md border p-4 pr-8 shadow-lg",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            t.kind === "success" && "border-green-500/50 bg-green-50 text-green-900",
            t.kind === "error" && "border-destructive bg-destructive text-destructive-foreground",
            t.kind === "warning" && "border-yellow-500/50 bg-yellow-50 text-yellow-900",
            t.kind === "default" && "border-border bg-background",
          )}
        >
          <div className="flex-1">
            {t.title && <ToastPrimitive.Title className="text-sm font-semibold">{t.title}</ToastPrimitive.Title>}
            {t.description && (
              <ToastPrimitive.Description className="text-sm opacity-90">{t.description}</ToastPrimitive.Description>
            )}
          </div>
          {t.action && (
            <ToastPrimitive.Action
              altText={t.action.label}
              onClick={t.action.onClick}
              className="rounded-md border border-current px-2 py-1 text-xs"
            >
              {t.action.label}
            </ToastPrimitive.Action>
          )}
          <ToastPrimitive.Close className="absolute right-2 top-2 rounded-sm opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
